import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { SubscriptionGate } from '@/components/SubscriptionGate'

// Páginas de auth carregadas direto (são pequenas)
import LoginPage from '@/modules/auth/LoginPage'
import RecuperarSenhaPage from '@/modules/auth/RecuperarSenhaPage'

// Página pública de confirmação de escala (sem login)
const ConfirmInvitePage = lazy(() => import('@/modules/confirm/ConfirmInvitePage'))
// Página pública de auto-cadastro de membro da equipe (sem login)
const TeamSignupPublicPage = lazy(() => import('@/modules/team/TeamSignupPublicPage'))
const AceitarContratoPage = lazy(() => import('@/modules/contracts/AceitarContratoPage'))
const ContratoPdfPage = lazy(() => import('@/modules/contracts/ContratoPdfPage'))
const ConfirmarPresencaPage = lazy(() => import('@/modules/guest-list/ConfirmarPresencaPage'))
const GuestListPage = lazy(() => import('@/modules/guest-list/GuestListPage'))
const EventPrintPage = lazy(() => import('@/modules/events/EventPrintPage'))

// Página de relatório financeiro mensal (autenticada, mas fora do AppLayout pra impressão limpa)
const FinanceReportPage = lazy(() => import('@/modules/finance/FinanceReportPage'))

// Página do contrato individual de aluno formando (também fora do AppLayout)
const ContratoFormaturaPage = lazy(() => import('@/modules/formaturas/ContratoFormaturaPage'))

// Comprovante de uma parcela individual de aluno formando
const ComprovanteFormaturaPage = lazy(() => import('@/modules/formaturas/ComprovanteFormaturaPage'))
// Recibo em nome da ESCOLA (turma acertada direto com a instituição)
const ComprovanteEscolaPage = lazy(() => import('@/modules/formaturas/ComprovanteEscolaPage'))
// Lista de convidados (por aluno ou geral) — A4 imprimível
const ListaConvidadosPage = lazy(() => import('@/modules/formaturas/ListaConvidadosPage'))
const ReciboFormaturaPublicoPage = lazy(() => import('@/modules/formaturas/ReciboFormaturaPublicoPage'))
// Contrato de formatura PÚBLICO — aluno abre pelo link do WhatsApp (sem login)
const ContratoFormaturaPublicoPage = lazy(() => import('@/modules/formaturas/ContratoFormaturaPublicoPage'))
const ExtratoAlunoPage = lazy(() => import('@/modules/formaturas/ExtratoAlunoPage'))
const ExtratoAlunoPublicoPage = lazy(() => import('@/modules/formaturas/ExtratoAlunoPublicoPage'))
const ExtratoTurmaPage = lazy(() => import('@/modules/formaturas/ExtratoTurmaPage'))

// Recibo/comprovante em bobina térmica (58/80mm) de um lançamento financeiro
const ReciboTermicoPage = lazy(() => import('@/modules/finance/ReciboTermicoPage'))
// Recibo de pagamento de evento — PÚBLICO, aberto pelo link do WhatsApp
const ReciboEventoPublicoPage = lazy(() => import('@/modules/finance/ReciboEventoPublicoPage'))
// Comprovante de pagamento a fornecedor/prestador (o outro lado do recibo acima)
const ComprovantePagamentoPublicoPage = lazy(() => import('@/modules/finance/ComprovantePagamentoPublicoPage'))

// Orçamento renderizado a partir do template do tenant
const OrcamentoModeloPage = lazy(() => import('@/modules/proposals/OrcamentoModeloPage'))

// Módulos com lazy loading — cada chunk separado, ganho enorme no bundle inicial
const DashboardPage   = lazy(() => import('@/modules/dashboard/DashboardPage'))
const ClientsPage     = lazy(() => import('@/modules/clients/ClientsPage'))
const EventsPage      = lazy(() => import('@/modules/events/EventsPage'))
const ProposalsPage   = lazy(() => import('@/modules/proposals/ProposalsPage'))
const ContractsPage   = lazy(() => import('@/modules/contracts/ContractsPage'))
const FinancePage     = lazy(() => import('@/modules/finance/FinancePage'))
const SuppliersPage   = lazy(() => import('@/modules/suppliers/SuppliersPage'))
const BuffetPage      = lazy(() => import('@/modules/buffet/BuffetPage'))
const TeamPage        = lazy(() => import('@/modules/team/TeamPage'))
const ReportsPage     = lazy(() => import('@/modules/reports/ReportsPage'))
const SupportPage     = lazy(() => import('@/modules/support/SupportPage'))
const WhatsAppPage    = lazy(() => import('@/modules/whatsapp/WhatsAppPage'))
const SettingsPage    = lazy(() => import('@/modules/settings/SettingsPage'))
const AdminPanelPage  = lazy(() => import('@/modules/admin/AdminPanelPage'))
const FormaturasPage  = lazy(() => import('@/modules/formaturas/FormaturasPage'))
const SchedulePage    = lazy(() => import('@/modules/schedule/SchedulePage'))
const LeadsPage       = lazy(() => import('@/modules/leads/LeadsPage'))
const ImportarPage    = lazy(() => import('@/modules/import/ImportarPage'))
const OfxImportPage   = lazy(() => import('@/modules/import/ofx/OfxImportPage'))
const PacotesPage     = lazy(() => import('@/modules/proposals/PacotesPage'))
const BriefingMusicalPage = lazy(() => import('@/modules/events/BriefingMusicalPage'))

const Loading = () => (
  <div className="p-8 text-center text-text-secondary">Carregando módulo…</div>
)

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* públicas */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/recuperar-senha" element={<RecuperarSenhaPage />} />
        <Route path="/confirmar/:token" element={<ConfirmInvitePage />} />
        {/* Auto-cadastro de membro da equipe — pessoa preenche dados + foto */}
        <Route path="/cadastro-equipe/:token" element={<TeamSignupPublicPage />} />
        <Route path="/aceitar-contrato/:token" element={<AceitarContratoPage />} />

        {/* PDF público de contrato (cliente abre pelo link) */}
        <Route path="/contrato-pdf-publico/:token" element={<ContratoPdfPage />} />

        {/* RSVP — confirmação de presença pública */}
        <Route path="/presenca/:token" element={<ConfirmarPresencaPage />} />

        {/* Comprovante de pagamento de formatura — PÚBLICO (aluno abre pelo
            link do WhatsApp, sem login). Lê o snapshot payment_receipts/{token}. */}
        <Route path="/recibo-formatura/:token" element={<ReciboFormaturaPublicoPage />} />

        {/* Recibo de pagamento de EVENTO — PÚBLICO (cliente abre pelo link do
            WhatsApp, sem login). Lê o snapshot event_receipts/{token}. */}
        <Route path="/recibo-evento/:token" element={<ReciboEventoPublicoPage />} />

        {/* Comprovante de pagamento a fornecedor/prestador — PÚBLICO (ele abre
            pelo link do WhatsApp, sem login). Lê o mesmo snapshot
            event_receipts/{token}, gravado com finance_kind 'contas_pagar'. */}
        <Route path="/comprovante-pagamento/:token" element={<ComprovantePagamentoPublicoPage />} />

        {/* Contrato de formatura — PÚBLICO (aluno abre pelo link do WhatsApp,
            sem login). Lê o snapshot formatura_contracts/{token}. */}
        <Route path="/contrato-formatura-publico/:token" element={<ContratoFormaturaPublicoPage />} />

        {/* Extrato do formando — PÚBLICO (responsável abre pelo link do
            WhatsApp, sem login). Lê o snapshot formatura_extratos/{token}. */}
        <Route path="/extrato-formatura-publico/:token" element={<ExtratoAlunoPublicoPage />} />

        {/* Relatório financeiro — autenticado mas fora do AppLayout (visual de PDF) */}
        <Route
          path="/relatorio-financeiro/:ym"
          element={
            <ProtectedRoute>
              <FinanceReportPage />
            </ProtectedRoute>
          }
        />

        {/* Contrato de formatura — A4 pra impressão/PDF */}
        <Route
          path="/contrato-formatura/:formaturaId/:alunoId"
          element={
            <ProtectedRoute>
              <ContratoFormaturaPage />
            </ProtectedRoute>
          }
        />

        {/* Comprovante de pagamento individual da parcela (A4 imprimível) */}
        <Route
          path="/comprovante-formatura/:formaturaId/:alunoId/:pagamentoId"
          element={
            <ProtectedRoute>
              <ComprovanteFormaturaPage />
            </ProtectedRoute>
          }
        />

        {/* Recibo em nome da ESCOLA — lê o pagamento gravado na formatura */}
        <Route
          path="/comprovante-escola-formatura/:formaturaId/:pagamentoId"
          element={
            <ProtectedRoute>
              <ComprovanteEscolaPage />
            </ProtectedRoute>
          }
        />
        {/* Link antigo (sem id do pagamento): mostra onde achar o recibo */}
        <Route
          path="/comprovante-escola-formatura/:formaturaId"
          element={
            <ProtectedRoute>
              <ComprovanteEscolaPage />
            </ProtectedRoute>
          }
        />

        {/* Extrato do formando (A4 imprimível + envio por WhatsApp) */}
        <Route
          path="/extrato-formatura/:formaturaId/:alunoId"
          element={<ProtectedRoute><ExtratoAlunoPage /></ProtectedRoute>}
        />

        {/* Extrato coletivo da turma — INTERNO, sem link público (LGPD) */}
        <Route
          path="/extrato-formatura-turma/:formaturaId"
          element={<ProtectedRoute><ExtratoTurmaPage /></ProtectedRoute>}
        />

        {/* Lista de convidados — geral da turma ou de um aluno (A4 imprimível) */}
        <Route
          path="/lista-convidados-formatura/:formaturaId"
          element={<ProtectedRoute><ListaConvidadosPage /></ProtectedRoute>}
        />
        <Route
          path="/lista-convidados-formatura/:formaturaId/:alunoId"
          element={<ProtectedRoute><ListaConvidadosPage /></ProtectedRoute>}
        />

        {/* Recibo em bobina térmica (58/80mm) de um lançamento financeiro */}
        <Route
          path="/recibo-termico/:kind/:id"
          element={
            <ProtectedRoute>
              <ReciboTermicoPage />
            </ProtectedRoute>
          }
        />

        {/* Orçamento renderizado do modelo do tenant (A4 imprimível) */}
        <Route
          path="/orcamento-modelo/:eventId"
          element={
            <ProtectedRoute>
              <OrcamentoModeloPage />
            </ProtectedRoute>
          }
        />

        {/* PDF do contrato (master/owner) */}
        <Route
          path="/contrato-pdf/:contractId"
          element={
            <ProtectedRoute>
              <ContratoPdfPage />
            </ProtectedRoute>
          }
        />

        {/* Impressão completa do evento (autenticada, fora do AppLayout) */}
        <Route
          path="/imprimir-evento/:eventId"
          element={
            <ProtectedRoute>
              <EventPrintPage />
            </ProtectedRoute>
          }
        />

        {/* Briefing musical pra impressão (DJ leva no dia) */}
        <Route
          path="/briefing-musical/:eventId"
          element={
            <ProtectedRoute>
              <BriefingMusicalPage />
            </ProtectedRoute>
          }
        />

        {/* área autenticada — path /* permite que <Routes> aninhado pegue qualquer caminho */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <SubscriptionGate>
              <AppLayout>
                <Routes>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="clientes/*" element={<ClientsPage />} />
                  <Route path="eventos/*" element={<EventsPage />} />
                  <Route path="propostas/*" element={<ProposalsPage />} />
                  <Route path="contratos/*" element={<ContractsPage />} />
                  <Route path="financeiro/*" element={<FinancePage />} />
                  <Route path="fornecedores/*" element={<SuppliersPage />} />
                  <Route path="buffet/*" element={<BuffetPage />} />
                  <Route path="equipe/*" element={<TeamPage />} />
                  <Route path="cronograma/*" element={<SchedulePage />} />
                  <Route path="leads/*" element={<LeadsPage />} />
                  <Route path="lista-presenca/*" element={<GuestListPage />} />
                  <Route path="relatorios/*" element={<ReportsPage />} />
                  <Route path="whatsapp/*" element={<WhatsAppPage />} />
                  <Route path="suporte/*" element={<SupportPage />} />
                  <Route path="formaturas/*" element={<FormaturasPage />} />
                  {/* Importar: dono do tenant ou master (não basta esconder no menu) */}
                  <Route path="importar" element={
                    <ProtectedRoute requiredRole={['master', 'tenant_owner']}>
                      <ImportarPage />
                    </ProtectedRoute>
                  } />
                  <Route path="importar/ofx" element={
                    <ProtectedRoute requiredRole={['master', 'tenant_owner']}>
                      <OfxImportPage />
                    </ProtectedRoute>
                  } />
                  <Route path="pacotes-orcamento" element={<PacotesPage />} />
                  <Route path="configuracoes/*" element={<SettingsPage />} />

                  {/* só master */}
                  <Route
                    path="admin/*"
                    element={
                      <ProtectedRoute requiredRole={['master']}>
                        <AdminPanelPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppLayout>
              </SubscriptionGate>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  )
}
